import React, { useState, useRef, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { css, StyleSheet } from 'aphrodite/no-important';
import { TextInput, PopupMenu, Button } from 'quantux-ui';
import { BASE_URL, getHighlightedText, titleCase } from '../../utils';
import { blue, gray } from '../../theme';
import CaseUserCard from '../revenue-cycle-management/case-user-card';
import WarningIcon from '../svg-icons/warning-icon';
import CriticalWarningIcon from '../svg-icons/critical-warning-icon';

const PracticeProviderCard = (props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchMenu, setShowSearchMenu] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [providers, setProviders] = useState([]);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  // __ Props ___________________________________________________________
  const { data, placeholderMsg = '', searchRoute = 'searchMedProsAnesthesiologists' } = props;
  const { practiceName, addedProviders, taxonomy, _id } = data;

  // __ Network Ops _____________________________________________________
  const searchProviders = (value) => {
    const url = new URL(`${BASE_URL}/users/${searchRoute}`);
    const params = {
      searchTerm: value.trim(),
      providersOnly: true,
      practiceId: _id || data.practiceId,
      enforcePracticeRelation: true
    };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setShowSearchMenu(true);
        setSearchResults(data.users);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedSearchProviders = useCallback(debounce(value => searchProviders(value), 800), []);

  // __ Refs ____________________________________________________________
  const searchMenuRef = useRef();

  // __ Effects _________________________________________________________
  useEffect(() => {
    const { addedProviders } = props.data;
    setProviders(addedProviders);
  }, [props.data]);

  useEffect(() => {
    if (searchTerm.length === 0) {
      setSearchResults([]);
    }
    if (searchTerm.trim().length > 0) {
      debouncedSearchProviders(searchTerm);
    }
  }, [searchTerm]);

  // __ Styles __________________________________________________________
  const styles = StyleSheet.create({
    searchPopupMenuContainer: {
      width: '358px',
      background: blue[900],
      paddingBottom: '1px',
      borderRadius: '5px',
      overflowY: 'auto',
      maxHeight: '200px'
    },
    menuItem: {
      cursor: 'pointer',
      // background: '#103860',
      background: gray[900],
      borderRadius: '8px',
      color: gray[300],
      padding: '4px',
      lineHeight: '16px',
      fontSize: '14px',
      marginBottom: '2px',
      ':hover': {
        background: '#184878',
        color: 'white'
      },
      ':last-child': {
        borderBottom: '0'
      }
    },
    menuItemAvatarImg: {
      height: '32px',
      maxHeight: '32px',
      borderRadius: '4px'
    },
    headerText: {
      fontSize: '14px',
      lineHeight: '16px'
    }
  });

  return (
    <div
      className='mt-2'
      style={{
        background: blue[700],
        borderRadius: '4px',
        padding: '8px'
      }}
    >
      {/* Delete warning */}
      {showDeleteWarning &&
        <div className='p-2' style={{ width: '100%', height: '100%' }}>
          {/* Warning message */}
          <div className='pt-2 pb-2'>
            Do you want to delete practice?
          </div>
          <div
            className='fx mt-4 pt-2'
            style={{
              justifyContent: 'flex-end',
              borderTop: `1px solid ${gray[500]}`
            }}
          >
            <Button
              btnType='primary'
              isOutlineBtn
              className='mr-2'
              onClick={() => {
                setShowDeleteWarning(false);
              }}
            >
              Cancel
            </Button>
            <Button
              btnType='warning'
              onClick={() => {
                props.onDeletePractice(props.index);
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      }
      {/* Header */}
      <div
        className='fx'
        style={{
          display: showDeleteWarning ? 'none' : '',
          justifyContent: 'space-between',
          height: '40px'
        }}
      >
        <div className={css(styles.headerText)}>
          <div style={{ fontWeight: '700' }}>{practiceName}</div>
          <div>{taxonomy}</div>
        </div>
        {/* Anesthesia practice delete button */}
        <div onClick={() => setShowDeleteWarning(true)}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" fill="none">
            <path fill="#96C8FF" fillRule="evenodd" d="M20.755 3.259a.88.88 0 0 0-.614-.25h-4.313V1.657C15.828.743 15.089 0 14.18 0H6.818C5.91 0 5.171.743 5.171 1.657V3.01H.837A.84.84 0 0 0 0 3.851c0 .466.374.842.837.842h.738v14.65c0 .914.738 1.657 1.647 1.657h14.79c.909 0 1.647-.743 1.647-1.657V4.693h.481c.46 0 .86-.376.86-.842a.83.83 0 0 0-.245-.592ZM2.49 20.675a1.712 1.712 0 0 1-.103-.1c.032.035.067.067.103.1Zm.123.028c.033.036.067.073.1.1-.02-.017-.04-.038-.061-.06l-.04-.04ZM14.174 1.664H6.816v1.361h7.358v-1.36Zm3.835 17.675H3.222V4.69H18.009V19.34ZM9.647 8.362c0-.468.377-.846.842-.846.465 0 .84.378.84.846v7.853a.843.843 0 0 1-.84.846.843.843 0 0 1-.842-.846V8.362Zm-3.715-.846a.843.843 0 0 0-.84.846v7.853c0 .468.375.846.84.846a.843.843 0 0 0 .842-.846V8.362a.844.844 0 0 0-.842-.846Zm8.225.846c0-.468.376-.846.84-.846.466 0 .842.378.842.846v7.853a.843.843 0 0 1-.841.846.843.843 0 0 1-.841-.846V8.362Z" clipRule="evenodd" />
          </svg>
        </div>
      </div>

      {/* Provider Search */}
      <div
        style={{
          position: 'relative',
          display: showDeleteWarning ? 'none' : ''
        }}
      >
        <div
          ref={searchMenuRef}
          style={{ position: 'relative' }}
        >
          <TextInput
            value={searchTerm}
            placeholder={placeholderMsg}
            className='mt-2'
            onChange={(e) => {
              const { value } = e.target;
              setSearchTerm(value);
            }}
            style={{
              width: '341px',
              fontSize: '14px',
              padding: '0 4px',
              color: blue[200],
              borderRadius: '4px'
            }}
          />
          {showSearchMenu &&
            <PopupMenu
              top={45}
              left={-1}
              zIndex='1550'
              position="absolute"
              visible={showSearchMenu}
              parent={searchMenuRef}
              onOutsideClick={() => { setShowSearchMenu(false); }}
            >
              <div className={css(styles.searchPopupMenuContainer)}>
                {Array.isArray(searchResults) && searchResults.length > 0 &&
                  searchResults.map((el) => (
                    <div
                      key={el._id}
                      className={`fx fx__v-center ${css(styles.menuItem)}`}
                      style={{ height: '40px', width: '341px' }}
                      onClick={() => {
                        setSearchTerm('');
                        if (props.data.addedProviders.findIndex((item) => item._id === el._id) < 0) {
                          props.onAddProvider(el, props.index);
                        } else {
                          console.log('Provider already exists!');
                        }
                      }}
                    >
                      <div className='fx fx__v-center'>
                        <img
                          src={el.avatarImageUrl}
                          className={css(styles.menuItemAvatarImg)}
                        />
                        <div className={`ml-2 ${css(styles.menuItemText)}`}>
                          {/* {`${titleCase(el.displayName)}, ${el.credentials}`} */}
                          {getHighlightedText(el.displayName, searchTerm)}
                          {`, ${el.credentials}`}
                          {/* {`${titleCase(getHighlightedText(el.displayName, searchTerm))}, ${el.credentials}`} */}
                          <div>
                            {el.specialty}
                          </div>
                        </div>
                      </div>
                      {/* {el.displayName} */}
                    </div>
                  ))
                }
              </div>
            </PopupMenu>
          }
        </div>
        {providers.length === 0 &&
          <div className='fx fx__v-center mt-4 mb-2'>
            {/* <WarningIcon height={20} /> */}
            {!props.isDashboard
              ? <WarningIcon height={20} />
              : <CriticalWarningIcon height={20} />
            }
            <span className='ml-2'>
              {props.warningText}
            </span>
          </div>
        }
        {providers.length > 0 &&
          providers.map((el) => {
            // console.log(el);
            return (
              <CaseUserCard
                className='mt-1'
                key={`selected__${el._id}__${practiceName}`}
                avatarImageUrl={el.avatarImageUrl || 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg'}
                showCloseIcon
                showAlert={false}
                dataId={el._id}
                style={{ height: '54px' }}
                onClose={(user) => {
                  props.onDeleteProvider(props.index, user);
                }}
              >
                <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                  <div className='bold-text'>
                    {`${el.providerFirstName || el.firstName} ${el.providerLastName || el.lastName}`}
                  </div>
                  <div>{el.specialty}</div>
                  {/* <div>Anesthesiologist</div> */}
                </div>
              </CaseUserCard>
            );
          })
        }
      </div>
    </div>
  );
};

export default PracticeProviderCard;
